import React from "react";

const RELOAD_KEY = "ds:chunk-reload";

export function isStaleChunkError(error: unknown): boolean {
  const message = String((error as Error)?.message || error || "");
  return (
    message.includes("Failed to fetch dynamically imported module")
    || message.includes("Importing a module script failed")
    || message.includes("error loading dynamically imported module")
    || message.includes("Loading chunk")
    || message.includes("Expected a JavaScript")
    || message.includes("MIME type")
    || message.includes("module script")
  );
}

/**
 * One-shot page reload when a Vite hashed chunk is missing after deploy.
 * Returns true if a reload was triggered (caller should stop work).
 */
export function reloadOnceOnStaleChunk(error?: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (error != null && !isStaleChunkError(error)) return false;

  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
  } catch {
    alreadyReloaded = false;
  }
  if (alreadyReloaded) return false;

  try {
    sessionStorage.setItem(RELOAD_KEY, "1");
  } catch {
    // ignore
  }
  window.location.reload();
  return true;
}

export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // ignore
  }
}

/**
 * Dynamic import that recovers from stale Vite chunk hashes after deploy
 * (including MIME text/html SPA fallback for missing /assets/*.js).
 */
export async function importWithReload<T>(factory: () => Promise<T>): Promise<T> {
  try {
    const mod = await factory();
    clearChunkReloadFlag();
    return mod;
  } catch (error) {
    if (reloadOnceOnStaleChunk(error)) {
      return new Promise(() => undefined) as Promise<T>;
    }
    throw error;
  }
}

/**
 * React.lazy wrapper that recovers from stale Vite chunk hashes after deploy
 * ("Failed to fetch dynamically imported module" / MIME text/html on missing assets).
 */
export function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithReload(factory));
}
